import { useForm, router, usePage } from "@inertiajs/react";
import { useMemo, useState, useCallback } from "react";
import clsx from "clsx";
import type { HackatimeProject } from "@/interfaces/hackatime_project";
import type { Project } from "@/interfaces/project";
import type { ProjectTag } from "@/interfaces/project_tag";
import type { ShippableEvent } from "@/interfaces/shippable_event";
import type { SharedProps } from "@/types";
import formatTime from "@/utils/formatTime";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import { Field, Input, Textarea, Select } from "@/components/ui/Field";

// "Nothing" used to be the empty first option, which made it the default for
// anyone allowed to pick it -- so an organizer who also had the grant shipped
// for no event without ever choosing to. A travel grant is now a value you
// select on purpose, and "" means you haven't answered yet.
const TRAVEL_GRANT = "travel";

interface Props {
  hackatime_projects: HackatimeProject[];
  tags: ProjectTag[];
  project?: Project;
  shippable_events?: ShippableEvent[];
  // HQ's travel-grant unlock. Everyone else has to name the Boba Bash their
  // hours pay into; a travel grant is the only project allowed to name none.
  travel_grant?: boolean;
  tutorial?: boolean;
}

interface ShipCheck {
  label: string;
  passed: boolean;
  loading?: boolean;
  failHint?: string;
  hint?: string;
  hintLink?: string;
  optional?: boolean;
}

function CheckMark({ check }: { check: ShipCheck }) {
  const state = check.loading
    ? "loading"
    : check.passed
      ? "passed"
      : check.optional
        ? "skipped"
        : "failed";

  return (
    <span
      className={clsx(
        "font-display text-caption flex h-5 w-5 shrink-0 items-center justify-center border-2 leading-none font-bold",
        state === "passed" && "border-line bg-leaf text-white",
        state === "failed" && "border-sunset-orange text-sunset-orange",
        (state === "loading" || state === "skipped") &&
          "border-line/40 text-ink-soft",
      )}
    >
      {state === "loading"
        ? "…"
        : state === "passed"
          ? "✓"
          : state === "skipped"
            ? "-"
            : "✗"}
    </span>
  );
}

function PreShipChecklist({
  open,
  checks,
  repoChecking,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  checks: ShipCheck[];
  repoChecking: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const allPassed =
    checks.every((c) => c.passed || c.optional) && !repoChecking;

  return (
    <Modal
      open={open}
      title="pre-ship-checklist.txt"
      onClose={onCancel}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Not yet
          </Button>
          <Button type="button" onClick={onConfirm} disabled={!allPassed}>
            Ship it
          </Button>
        </>
      }
    >
      <ul className="flex flex-col gap-3">
        {checks.map((check) => (
          <li key={check.label} className="flex flex-col gap-0.5">
            <div className="flex items-center gap-3">
              <CheckMark check={check} />
              <span
                className={clsx(
                  "text-body",
                  check.loading || (!check.passed && check.optional)
                    ? "text-ink-soft"
                    : check.passed
                      ? "text-ink"
                      : "text-sunset-orange font-bold",
                )}
              >
                {check.label}
              </span>
            </div>
            {!check.loading && !check.passed && check.failHint && (
              <span className="text-caption text-sunset-orange ml-8">
                {check.failHint}
              </span>
            )}
            {check.hint && (
              <span className="text-caption text-ink-soft ml-8">
                {check.hintLink ? (
                  <a
                    href={check.hintLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-link underline"
                  >
                    {check.hint}
                  </a>
                ) : (
                  check.hint
                )}
              </span>
            )}
          </li>
        ))}
      </ul>
    </Modal>
  );
}

export default function ProjectForm({
  hackatime_projects,
  tags,
  project,
  shippable_events = [],
  travel_grant = false,
  tutorial,
}: Props) {
  const { props } = usePage<SharedProps>();
  // Every answer this person could give. An organizer sees their events, a
  // travel-grant holder with no event sees only the grant, and HQ sees both.
  const attributionChoices = [
    ...shippable_events.map((event) => String(event.id)),
    ...(travel_grant ? [TRAVEL_GRANT] : []),
  ];
  // Preselected only when there is nothing to choose between. Picking one of
  // several on someone's behalf is how hours end up paying the wrong event.
  const soleChoice =
    attributionChoices.length === 1 ? attributionChoices[0] : "";

  const { data, setData, post, patch, processing, errors, progress } = useForm({
    title: project?.title ?? "",
    desc: project?.desc ?? "",
    repo_link: project?.repo_link ?? "",
    demo_link: project?.demo_link ?? "",
    hackatime_project_keys: project?.hackatime_projects ?? ([] as number[]),
    screenshot: (project?.screenshot ? 0 : null) as File | 0 | null,
    tags: project?.tags ?? ([] as number[]),
    ai_declaration: project?.ai_declaration ?? "",
    // "" is a real answer here - it clears the attribution - so it's kept as a
    // string and resolved server-side against the events this person staffs.
    event_id: project?.event_id
      ? String(project.event_id)
      : project && travel_grant
        ? TRAVEL_GRANT
        : soleChoice,
  });

  const disabled = project?.aasm_state === "submitted";
  const idvVerified = props.user.verification_status === "verified";
  const isAdmin = props.user.is_admin;
  const hourlyCents = props.program.budget_per_hour_cents;
  const [showShipChecklist, setShowShipChecklist] = useState(false);
  const [repoChecking, setRepoChecking] = useState(false);
  const [repoAccessible, setRepoAccessible] = useState<boolean | null>(null);
  const [hasReadme, setHasReadme] = useState<boolean | null>(null);
  const [isGithub, setIsGithub] = useState<boolean | null>(null);

  const hasTitle = data.title.trim().length > 0;
  const hasDescription = data.desc.trim().length > 0;
  const hasDemoLink = !!data.demo_link;
  const hasScreenshot = data.screenshot !== null;
  const hasHackatimeProject =
    Array.isArray(data.hackatime_project_keys) &&
    data.hackatime_project_keys.length > 0;
  const hasRepoLink = data.repo_link.trim().length > 0;
  const hasAiDeclaration = data.ai_declaration.trim().length > 0;
  // The attribution is the whole point of the project, so it's a ship check like
  // any other rather than something the server refuses at the last moment.
  const hasEvent = data.event_id !== "";

  const shipChecks: ShipCheck[] = [
    {
      label: "Boba Bash it's for",
      passed: hasEvent,
      failHint: travel_grant
        ? "Pick the event your hours pay into, or say it's a travel grant."
        : "Pick the event your hours pay into.",
    },
    { label: "Project name", passed: hasTitle },
    { label: "Description", passed: hasDescription },
    {
      label: "AI declaration",
      passed: hasAiDeclaration,
      hint: "Say clearly how you used it.",
      optional: true,
    },
    { label: "Demo link", passed: hasDemoLink },
    { label: "Screenshot", passed: hasScreenshot },
    {
      label: "Public code repository",
      passed: hasRepoLink && (repoAccessible === true || !isGithub),
      loading: repoChecking,
      failHint: "Check that your repository is public.",
      hint:
        hasRepoLink && !isGithub
          ? "This repository isn't on GitHub - double check that it exists."
          : "",
    },
    {
      label: "Readme worth reading",
      passed: hasRepoLink && (hasReadme === true || !isGithub),
      loading: repoChecking,
      hint: props.program?.guide_url ? "Unsure? Read the guide." : "",
      hintLink: props.program?.guide_url ?? undefined,
    },
    { label: "Hackatime project attached", passed: hasHackatimeProject },
  ];

  const checkRepo = useCallback(async () => {
    if (!project || !hasRepoLink) {
      setRepoAccessible(false);
      setHasReadme(false);
      setIsGithub(false);
      return;
    }
    setRepoChecking(true);
    try {
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");
      const res = await fetch(`/projects/${project.id}/check_repo`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken || "",
          Accept: "application/json",
        },
      });
      const json = await res.json();
      setRepoAccessible(json.accessible);
      setHasReadme(json.has_readme);
      setIsGithub(json.is_github);
    } catch {
      setRepoAccessible(false);
      setHasReadme(false);
      setIsGithub(false);
    } finally {
      setRepoChecking(false);
    }
  }, [project, hasRepoLink]);

  function submit(e: React.FormEvent) {
    e.preventDefault();

    if (project) {
      patch(`/projects/${project.id}`, { forceFormData: true });
    } else {
      post("/projects/", { forceFormData: true });
    }
  }

  function openShipChecklist(e: React.MouseEvent) {
    e.stopPropagation();
    setRepoAccessible(null);
    patch(`/projects/${project!.id}`, {
      forceFormData: true,
      onSuccess: () => {
        setShowShipChecklist(true);
        checkRepo();
      },
    });
  }

  function confirmShip() {
    setShowShipChecklist(false);
    patch(`/projects/${project!.id}`, {
      forceFormData: true,
      onFinish: () => router.patch(`/projects/${project!.id}/ship`),
    });
  }

  function deleteProject(e: React.MouseEvent) {
    e.stopPropagation();
    if (confirm("Are you sure you want to delete this project?")) {
      router.delete(`/projects/${project!.id}`);
    }
  }

  const sortedHackatimeProjects = useMemo(
    () =>
      [...hackatime_projects].sort((a, b) => b.total_seconds - a.total_seconds),
    [hackatime_projects],
  );

  const selectedSeconds = useMemo(
    () =>
      hackatime_projects
        .filter((hp) => data.hackatime_project_keys.includes(hp.id))
        .reduce((acc, hp) => acc + hp.total_seconds, 0),
    [hackatime_projects, data.hackatime_project_keys],
  );

  const canShip =
    project && project.reported_seconds > project.approved_seconds;

  return (
    <form onSubmit={submit} className="flex w-full flex-col gap-6">
      <Panel title={project ? "project.txt" : "new-project.txt"}>
        <div className="flex flex-col gap-5">
          <Field
            label="Title"
            hint="Give your project a name"
            error={errors.title}
          >
            <Input
              value={data.title}
              onChange={(e) => setData("title", e.target.value)}
              disabled={disabled}
              required
            />
          </Field>

          <Field
            label="Description"
            hint="What does it do, and what did you have to work out to build it?"
            error={errors.desc}
          >
            <Textarea
              className="h-32 resize-none"
              value={data.desc}
              onChange={(e) => setData("desc", e.target.value)}
              disabled={disabled}
              required
            />
          </Field>

          {tutorial && (
            <p className="text-caption text-ink-muted italic">
              You don't need the rest right now - but you will before you can
              ship.
            </p>
          )}

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Field
              label="Demo link"
              hint="Where someone can try it"
              error={errors.demo_link}
            >
              <Input
                type="url"
                value={data.demo_link}
                onChange={(e) => setData("demo_link", e.target.value)}
                disabled={disabled}
              />
            </Field>

            <Field
              label="Repository link"
              hint="Public source code"
              error={errors.repo_link}
            >
              <Input
                type="url"
                value={data.repo_link}
                onChange={(e) => setData("repo_link", e.target.value)}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (
                    value.startsWith("https://github.com/") &&
                    value.endsWith(".git")
                  ) {
                    setData("repo_link", value.slice(0, -4));
                  }
                }}
                disabled={disabled}
              />
            </Field>
          </div>

          <Field label="Screenshot" hint="One image that shows it working">
            {data.screenshot === 0 ? (
              <div className="border-line bg-surface-plain relative w-fit border-2 p-2">
                <img
                  src={project?.screenshot}
                  alt="Uploaded screenshot"
                  className="block max-h-52 w-auto max-w-full object-contain"
                />
                {!disabled && (
                  <button
                    type="button"
                    aria-label="Remove screenshot"
                    className="border-line bg-close font-display text-caption absolute top-3 right-3 flex h-6 w-6 cursor-pointer items-center justify-center border leading-none font-bold text-white transition-transform hover:scale-110"
                    onClick={() => setData("screenshot", null)}
                  >
                    ×
                  </button>
                )}
              </div>
            ) : (
              !disabled && (
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/tiff,image/webp,image/heic"
                  className="cursor-pointer"
                  onChange={(e) =>
                    setData("screenshot", e.target.files?.[0] ?? null)
                  }
                />
              )
            )}
            {progress && (
              <progress
                className="w-full"
                value={progress.percentage}
                max="100"
              >
                {progress.percentage}%
              </progress>
            )}
          </Field>
        </div>
      </Panel>

      <Panel title="hours.txt" tone="alt">
        <div className="flex flex-col gap-4">
          <Field
            label="Hackatime projects"
            hint="The hours these have logged are the hours this ship is worth. Pick every one you coded on."
          >
            <div className="border-line bg-surface-plain max-h-60 overflow-y-auto border-2">
              {sortedHackatimeProjects.length === 0 ? (
                <p className="text-caption text-ink-soft p-4">
                  {props.user.hackatime_id
                    ? "No unassigned Hackatime projects. Code for a bit and they'll show up here."
                    : "Connect Hackatime to pick up your logged hours."}
                </p>
              ) : (
                sortedHackatimeProjects.map((hp) => {
                  const isSelected = data.hackatime_project_keys.includes(
                    hp.id,
                  );
                  return (
                    <button
                      key={hp.id}
                      type="button"
                      disabled={disabled}
                      className={clsx(
                        "border-line/20 duration-fast ease-standard flex w-full cursor-pointer items-center justify-between gap-3 border-b px-4 py-2.5 text-left transition-colors last:border-b-0",
                        isSelected
                          ? "bg-goldenrod text-ink font-bold"
                          : "text-ink hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-40",
                      )}
                      onClick={() =>
                        setData(
                          "hackatime_project_keys",
                          isSelected
                            ? data.hackatime_project_keys.filter(
                                (id) => id !== hp.id,
                              )
                            : [...data.hackatime_project_keys, hp.id],
                        )
                      }
                    >
                      <span className="text-body min-w-0 truncate">
                        {hp.name}
                      </span>
                      <span className="font-display text-caption text-ink-muted shrink-0">
                        {formatTime(hp.total_seconds)}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </Field>

          {selectedSeconds > 0 && (
            <p className="text-caption text-ink-muted">
              {formatTime(selectedSeconds)} selected - worth about{" "}
              <strong className="text-ink">
                ${((selectedSeconds / 3600) * (hourlyCents / 100)).toFixed(2)}
              </strong>{" "}
              if a reviewer approves all of it.
            </p>
          )}

          {/* Every project belongs to one Boba Bash - that's the only reason
              hours are worth anything. A travel grant is the one exception,
              only HQ can hand one out, and it has to be chosen rather than
              fallen into, so the question is always asked. */}
          <Field
            label="Ship this for"
            hint={`Approved hours pay into this event's budget at $${(hourlyCents / 100).toFixed(2)} an hour.`}
            error={errors.event_id}
          >
            <Select
              value={data.event_id}
              onChange={(e) => setData("event_id", e.target.value)}
              disabled={disabled}
              required
            >
              {attributionChoices.length > 1 && (
                <option value="">Pick your Boba Bash…</option>
              )}
              {shippable_events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                  {event.city ? ` - ${event.city}` : ""}
                </option>
              ))}
              {/* Last, and never the default: the event is the ordinary
                    answer and this is the exception. */}
              {travel_grant && (
                <option value={TRAVEL_GRANT}>Nothing - travel grant</option>
              )}
            </Select>
            {shippable_events.length === 0 && !travel_grant && (
              <p className="text-caption text-sunset-orange">
                You're not on the team for any approved Boba Bash yet, so
                there's nothing to ship for. Ask your point of contact to add
                you.
              </p>
            )}
          </Field>
        </div>
      </Panel>

      <Panel title="details.txt" tone="alt">
        <div className="flex flex-col gap-5">
          <Field label="Tags" hint="Hold ⌘ or Ctrl to pick more than one">
            <Select
              multiple
              className="h-32"
              value={data.tags.map(String)}
              onChange={(e) =>
                setData(
                  "tags",
                  [...e.target.selectedOptions].map((o) => Number(o.value)),
                )
              }
              disabled={disabled}
            >
              {tags.map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="AI declaration"
            hint="Projects should be mostly human built. Say what you used AI for and how much."
          >
            <Textarea
              className="h-28 resize-none"
              value={data.ai_declaration}
              onChange={(e) => setData("ai_declaration", e.target.value)}
              disabled={disabled}
            />
          </Field>
        </div>
      </Panel>

      {disabled ? (
        <div className="border-line bg-surface-alt flex items-center gap-3 border-2 p-4">
          <Badge tone="info">Under review</Badge>
          <p className="text-caption text-ink-muted">
            Locked while a reviewer looks at it. You can edit again once there's
            a verdict.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button type="submit" disabled={processing} className="sm:flex-1">
            {project ? "Save changes" : "Create project"}
          </Button>

          {canShip && (
            <Button
              type="button"
              variant="secondary"
              onClick={openShipChecklist}
              disabled={processing || (!isAdmin && !idvVerified)}
              className="sm:flex-1"
            >
              {!isAdmin && !idvVerified
                ? "Verify to ship"
                : project.aasm_state === "approved" ||
                    project.aasm_state === "rejected"
                  ? "Re-ship"
                  : "Ship"}
            </Button>
          )}

          {project && (
            <Button
              type="button"
              variant="danger"
              onClick={deleteProject}
              disabled={processing}
            >
              Delete
            </Button>
          )}
        </div>
      )}

      <PreShipChecklist
        open={showShipChecklist}
        checks={shipChecks}
        repoChecking={repoChecking}
        onCancel={() => setShowShipChecklist(false)}
        onConfirm={confirmShip}
      />
    </form>
  );
}
